import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTypographyStore } from "@redux/slices/typographySlice";
import styled, { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import RequestViewCard from "@components/molecules/RequestViewCard";
import { ViewType } from "@components/molecules/PriceModeCard/types";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { useSelectedItems } from "../../../contexts/SelectedItemsContext";
import BulkItemEditModal from "@components/organisms/RequestViewParentCard/BulkItemEditModal";
import {
  isPendingRequestItem,
  showRequestItemFromSupplier,
} from "@scenes/requests/utils/utils";
import BulkInitSummaryList from "../BulkInitSummaryList";
import { useSelector } from "react-redux";
import OrderCartSummaryList from "@scenes/configurator/OrderCart/OrderCartSummaryList";
import ItemActionCard from "@scenes/requests/ItemActionCard";
import { PdpSections } from "@utils/customization";
import BuilderRequestSummaryList from "@scenes/jewelry-builder/components/BuilderRequestSummaryList";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { isPair, isParcel } from "@utils/product/productValidators";
import { StoneType } from "@scenes/bulk-request/Utils";
import { useConsolidatedStoneTypes } from "@redux/slices/globalSlice";

type RequestViewParentCardProps = {
  stockItemRequest: StockItemRequestClass;
  productType: any;
  validValue: boolean;
  onClickSupplierInfo: () => void;
  setDisabledButton: Dispatch<SetStateAction<boolean>>;
  isModifyScreen?: boolean;
  displayCheckbox?: boolean;
  itemList?: any[];
  saveBulkItemDetails?: any;
  fromRequestDetail?: boolean;
  orderCartRequest?: boolean;
  showListPrice?: boolean;
  isTotalOfferDiscountApplied?: boolean;
  hasAnyOfferDetails?: boolean;
  childValues?: [];
  skipUnread?: boolean;
  params?: {};
  quoteItemSelection?: boolean;
};

const RequestViewParentCard = ({
  stockItemRequest,
  productType,
  validValue,
  onClickSupplierInfo,
  setDisabledButton,
  isModifyScreen = false,
  itemList,
  saveBulkItemDetails,
  fromRequestDetail,
  orderCartRequest,
  isTotalOfferDiscountApplied,
  showListPrice,
  hasAnyOfferDetails,
  childValues,
  skipUnread,
  params,
  quoteItemSelection = false,
}: RequestViewParentCardProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const { t } = useTranslation();
  const { selectedItems, setSelectedItems } = useSelectedItems();
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const { enableJewelryBuilder } = useOrgSettingsSlice();
  const isBuilderItem =
    enableJewelryBuilder &&
    stockItemRequest?.secondary_request_type === PdpSections.JEWELRY_BUILDER;
  const { getPriceMode } = useUserInfo();
  let isTotalPriceApply =
    stockItemRequest?.additional_attributes?.total_offer_price_applied;
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const { enableJewelryHybridMode } = useOrgSettingsSlice();
  const hasChildren = !!stockItemRequest?.children?.length;
  const isConfiguratorItem =
    enableJewelryHybridMode &&
    (hasChildren
      ? stockItemRequest?.children.some(
          (item) => item?.configuration_details?.config !== undefined
        )
      : stockItemRequest?.configuration_details?.config);
  const customForm = useSelector(
    (state: any) => state.globalData?.app_settings?.custom_form
  );

  const pendingChildren = useMemo(
    () =>
      stockItemRequest?.children
        ?.filter((item: StockItemRequestClass) => isPendingRequestItem(item))
        .map((item: StockItemRequestClass) => item.id) || [],
    [stockItemRequest?.children, quoteItemSelection]
  );
  const selectableChildIds = pendingChildren;
  const selectableIdsKey = selectableChildIds.join(",");

  const showCheckbox = useMemo(
    () =>
      hasChildren &&
      (quoteItemSelection || selectableChildIds.length > 0) &&
      (quoteItemSelection ||
        stockItemRequest?.view_type !== ViewType.BuyerView ||
        fromRequestDetail ||
        isModifyScreen),
    [
      hasChildren,
      selectableChildIds,
      quoteItemSelection,
      stockItemRequest?.view_type,
      isModifyScreen,
      fromRequestDetail,
    ]
  );
  const consolidatedStoneTypes = useConsolidatedStoneTypes();

  useEffect(() => {
    if (hasChildren) {
      setSelectedItems(selectableChildIds);
    } else {
      setSelectedItems([]);
    }
    // Order details build a new stockItemRequest object on every parent render.
    // Depend on the request id and selectable line ids so a deselect is kept.
  }, [stockItemRequest?.id, selectableIdsKey, hasChildren]);

  const handleItemSelection = (itemId) => {
    if (!hasChildren) return;
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(itemId)) {
        return prevSelected.filter((id) => id !== itemId);
      } else {
        return [...prevSelected, itemId];
      }
    });
  };

  const checkBoxSelection = (checked, mItem) => {
    if (!hasChildren) return;
    const lineMatch = stockItemRequest?.children?.find(
      (child) =>
        child?.id === mItem?.stockItemRequestId || child?.id === mItem?.id
    );
    if (quoteItemSelection && lineMatch && !isPendingRequestItem(lineMatch)) {
      return;
    }
    const matchingItem = lineMatch
      ? lineMatch
      : isConfiguratorItem
      ? isModifyScreen
        ? stockItemRequest?.children?.find(
            (child) => child?.id === mItem?.stockItemRequestId
          )
        : stockItemRequest?.children?.find(
            (child) =>
              child?.[productType.productType].id === mItem?.stockItemRequestId
          )
      : stockItemRequest?.children?.find(
          (child) => child?.[productType.productType].id === mItem?.id
        );
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(matchingItem?.id)) {
        return prevSelected.filter((id) => id !== matchingItem?.id);
      } else {
        return [...prevSelected, matchingItem?.id];
      }
    });
  };

  const mItemList = isModifyScreen
    ? itemList
    : stockItemRequest.children?.map((child) => ({
        ...child[productType.productType],
        updated_price_per_carat: child.offer_price_per_carat,
        updated_total_sales_price: child.offer_total_sales_price,
        ...(child[productType.productType]?.total_sales_price !==
          child.offer_total_sales_price && {
          updated_discount_percent: child.offer_discount_percent ?? "-",
        }),
        ...(child?.quantity ? { quantity: child.quantity } : {}),
        ...(child?.offer_size ? { offer_size: child.offer_size } : {}),
      }));

  const stoneType = consolidatedStoneTypes?.enabled
    ? StoneType.SINGLE_STONES
    : isCustomApp && isParcel(mItemList?.[0]?.item ?? mItemList?.[0])
    ? StoneType.PARCELS
    : isPair(mItemList?.[0]?.item ?? mItemList?.[0])
    ? StoneType.PAIR
    : StoneType.SINGLE_STONES;

  return (
    <>
      <>
        <ItemActionCard
          stockItemRequest={stockItemRequest}
          productType={productType}
          setDisabledButton={setDisabledButton}
          showListPrice={!isTotalPriceApply}
          isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
          hasAnyOfferDetails={hasAnyOfferDetails}
          orderCartRequest={orderCartRequest}
          isModifyScreen={isModifyScreen}
          fromRequestDetailScreen={fromRequestDetail}
          quoteItemSelection={quoteItemSelection}
        />
        {hasChildren ? (
          !customForm && !orderCartRequest ? (
            stockItemRequest?.children.map(
              (item: StockItemRequestClass, index) => (
                <React.Fragment key={item.id}>
                  <>
                    <RequestViewCard
                      stockItemRequest={{
                        ...item?.[productType.productType],
                        offer_discount_percent: item.offer_discount_percent,
                        offer_price_per_carat: item.offer_price_per_carat,
                        offer_total_sales_price: item.offer_total_sales_price,
                        ...(quoteItemSelection || isPendingRequestItem(item)
                          ? {
                              id: item.id,
                              status: item.status,
                            }
                          : {}),
                        ...(item?.configuration_details
                          ? {
                              configuration_details: item.configuration_details,
                            }
                          : {}),
                      }}
                      productType={productType}
                      validValue={validValue}
                      isSelected={selectedItems.includes(item.id)}
                      onSelect={handleItemSelection}
                      showCheckbox={
                        quoteItemSelection
                          ? showCheckbox
                          : showCheckbox && isPendingRequestItem(item)
                      }
                      hasChildren={hasChildren}
                      showListPrice={showListPrice}
                      onEdit={(_item: any) => {
                        setSelectedItem(_item);
                        setOpen(true);
                      }}
                      isModifyScreen={isModifyScreen}
                    />
                  </>
                  {showRequestItemFromSupplier(item) && (
                    <RequestItemSupplierView onPress={onClickSupplierInfo}>
                      <VDBText
                        type={typography["Body-M"]}
                        color={theme["main-04"]}
                        textAlign="center"
                      >
                        {t(
                          "requestHistory.requestItemFromSupplier.description.text"
                        )}
                      </VDBText>
                    </RequestItemSupplierView>
                  )}
                  {index !== stockItemRequest?.children?.length - 1 && (
                    <Spacer size={SpaceType.Basic} />
                  )}
                </React.Fragment>
              )
            )
          ) : orderCartRequest ? (
            <OrderCartSummaryList
              itemList={itemList}
              priceMode={1}
              productType={productType}
              showRemoveIcon={false}
              orderCartRequest={
                orderCartRequest ? orderCartRequest : stockItemRequest
              }
              showCheckbox={quoteItemSelection && showCheckbox}
              quoteItemSelection={quoteItemSelection}
              checkBoxSelection={checkBoxSelection}
              stockItemRequest={stockItemRequest}
            />
          ) : isBuilderItem ? (
            <BuilderRequestSummaryList
              itemList={stockItemRequest.children}
              priceMode={getPriceMode()}
              productType={productType}
              isBuilderItem={isBuilderItem}
              params={params}
              display_configuration={stockItemRequest}
              display_configuration_v2={
                stockItemRequest?.jewelry?.display_configuration_v2 ?? []
              }
            />
          ) : (
            <>
              {isConfiguratorItem ? (
                <OrderCartSummaryList
                  itemList={stockItemRequest.children?.map((child) => {
                    const {
                      additional_attributes,
                      comments,
                      configuration_details,
                      id,
                      ...rest
                    } = child;
                    const dynamicData = child?.[productType.productType];
                    return {
                      ...rest,
                      [productType.productType]: {
                        ...dynamicData,
                        configuration_details: configuration_details,
                        comments: comments,
                        additional_attributes: additional_attributes,
                        stockItemRequestId: id,
                      },
                    }[productType.productType];
                  })}
                  priceMode={1}
                  productType={productType}
                  showRemoveIcon={false}
                  orderCartRequest={true}
                  isChildRequest={stockItemRequest?.parent_id !== 0}
                  fromModifyScreen={isModifyScreen}
                  requestType={stockItemRequest?.request_type}
                  saveBulkItemDetails={saveBulkItemDetails}
                  showCheckbox={showCheckbox}
                  checkBoxSelection={checkBoxSelection}
                />
              ) : (
                <BulkInitSummaryList
                  itemList={mItemList}
                  productType={productType}
                  requestType={stockItemRequest.request_type}
                  fromRequestModify={isModifyScreen}
                  saveBulkItemDetails={saveBulkItemDetails}
                  fromRequestDetail={fromRequestDetail}
                  stockItemRequest={stockItemRequest}
                  backgroundColor={theme["core-01"]}
                  showCheckbox={showCheckbox}
                  checkBoxSelection={checkBoxSelection}
                  showListPrice={showListPrice}
                  isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
                  viewType={stockItemRequest.view_type}
                  hasAnyOfferDetails={hasAnyOfferDetails}
                  childValues={childValues}
                  stoneType={stoneType}
                />
              )}
            </>
          )
        ) : (
          <>
            <SingleItemContainer>
              {isConfiguratorItem && orderCartRequest ? (
                <OrderCartSummaryList
                  itemList={[
                    {
                      ...stockItemRequest?.[productType.productType],
                      configuration_details:
                        stockItemRequest?.configuration_details,
                      additional_attributes:
                        stockItemRequest?.additional_attributes,
                    },
                  ]}
                  priceMode={1}
                  productType={productType}
                  showRemoveIcon={false}
                  orderCartRequest={true}
                  isChildRequest={stockItemRequest?.parent_id !== 0}
                  fromModifyScreen={isModifyScreen}
                  requestType={stockItemRequest?.request_type}
                  saveBulkItemDetails={saveBulkItemDetails}
                  checkBoxSelection={checkBoxSelection}
                  stockItemRequest={stockItemRequest}
                />
              ) : (
                <RequestViewCard
                  stockItemRequest={stockItemRequest}
                  productType={productType}
                  validValue={validValue}
                  isSelected={false}
                  onSelect={() => {}}
                  showCheckbox={false}
                  showListPrice={showListPrice}
                  isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
                  fromRequestDetailScreen={true}
                  hasAnyOfferDetails={hasAnyOfferDetails}
                  skipUnread={skipUnread}
                  isModifyScreen={isModifyScreen}
                  saveBulkItemDetails={saveBulkItemDetails}
                />
              )}
            </SingleItemContainer>
          </>
        )}
        {hasChildren && <Spacer size={8} />}
      </>
      {!orderCartRequest && (
        <BulkItemEditModal
          open={open}
          setOpen={setOpen}
          selectedItem={selectedItem}
          stockItemRequest={stockItemRequest}
          productType={productType}
        />
      )}
    </>
  );
};

const RequestItemSupplierView = styled.TouchableOpacity`
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.LargeDouble}px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-bottom-left-radius: ${SpaceType.Extra}px;
  border-bottom-right-radius: ${SpaceType.Extra}px;
`;

const SingleItemContainer = styled.View`
  padding: ${SpaceType.Basic}px ${SpaceType.Basic}px ${SpaceType.Double}px
    ${SpaceType.Basic}px;
`;

export default RequestViewParentCard;
